import { useEffect, useMemo, useState } from "react";
import syrupLogo from "./syrup-logo.svg";
import { T, fontDisplay, fontMono } from "./theme";
import { fmtDay, fmtMoney, fmtWeek, fyWeek, isoDate } from "./engine/dates";
import { applyAdjustments, scenarioWeeks } from "./engine/scenario";

/**
 * A scenario, as whoever received the link sees it.
 *
 * The commentary sits above the numbers because a scenario without its argument
 * is just a different set of figures with no way to tell why. Anything invented
 * for the scenario is marked as invented, so nobody mistakes the case being
 * made for the position being reported.
 */
export default function ScenarioView({ token }) {
  const [code, setCode] = useState(() => sessionStorage.getItem("inv_pw") || "");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);

  const load = async (c) => {
    setState("loading");
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/scenario", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, code: c }),
      });
      const json = await res.json();
      if (!res.ok) {
        if (json.needsCode) {
          setState("code");
          if (c) setError(json.error);
          return;
        }
        throw new Error(json.error || "Could not load");
      }
      if (c) sessionStorage.setItem("inv_pw", c);
      setData(json);
      setState("ready");
    } catch (e) {
      setError(e.message || String(e));
      setState("error");
    }
  };

  useEffect(() => {
    load(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const model = useMemo(() => {
    if (!data) return null;
    const adjusted = applyAdjustments(data.base, data.adjustments);
    const opts = {
      fromDate: data.fromDate,
      weeks: data.weeks,
      openingBalance: data.openingBalance,
    };
    return {
      weeks: scenarioWeeks(adjusted, opts),
      changed: data.adjustments?.length || 0,
    };
  }, [data]);

  if (state === "loading") {
    return <Shell><div style={{ color: T.muted, fontSize: 13 }}>Loading…</div></Shell>;
  }

  if (state === "error") {
    return (
      <Shell>
        <div style={{ color: T.muted, fontSize: 13 }}>{error || "Not found."}</div>
      </Shell>
    );
  }

  if (state === "code") {
    return (
      <Shell>
        <div style={{ width: "min(340px, 100%)", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 14 }}>
            This forecast needs the access code.
          </div>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(code)}
            placeholder="Access code"
            style={{
              width: "100%",
              fontSize: 16,
              padding: "12px 14px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              textAlign: "center",
            }}
          />
          <button
            onClick={() => load(code)}
            disabled={!code}
            style={{
              width: "100%",
              marginTop: 10,
              padding: "12px 14px",
              borderRadius: 8,
              border: "none",
              background: code ? T.accent : T.border,
              color: code ? "#fff" : T.muted,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            View
          </button>
          {error && <div style={{ color: T.cost, fontSize: 12, marginTop: 12 }}>{error}</div>}
        </div>
      </Shell>
    );
  }

  const { weeks } = model;
  const lowest = weeks.reduce((m, w) => (m == null || w.endBal < m.endBal ? w : m), null);
  const num = {
    padding: "6px 10px",
    textAlign: "right",
    fontFamily: fontMono,
    fontSize: 11,
    whiteSpace: "nowrap",
  };
  const nameCell = {
    padding: "6px 10px",
    fontSize: 12,
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    background: T.panel,
    zIndex: 1,
  };

  // One row per client for income, per category for costs — the same shape the
  // internal grid uses, so a scenario reads like the forecast it came from.
  const rows = (sign) => {
    const by = {};
    weeks.forEach((w, wi) => {
      for (const t of w.txns) {
        if (sign > 0 ? t.amount < 0 : t.amount >= 0) continue;
        const key = sign > 0 ? t.label : t.isManual ? t.label : t.category;
        by[key] = by[key] || { key, cells: Array(weeks.length).fill(0), speculative: false };
        by[key].cells[wi] += t.amount;
        if (t.speculative) by[key].speculative = true;
      }
    });
    return Object.values(by).sort((a, b) =>
      sign > 0 ? String(a.key).localeCompare(String(b.key)) : String(a.key).localeCompare(String(b.key))
    );
  };
  const income = rows(1);
  const costs = rows(-1);
  const colTotal = (rs, i) => rs.reduce((s, r) => s + r.cells[i], 0);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Manrope', system-ui, sans-serif", color: T.ink }}>
      <div style={{ padding: "18px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <img src={syrupLogo} alt="Syrup" style={{ height: 34 }} />
        <div style={{ fontSize: 12, color: T.muted }}>Cash flow · scenario</div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: T.muted }}>
          prepared {fmtDay(data.createdAt)}
        </div>
      </div>

      <div style={{ padding: "0 16px 30px" }}>
        <h1 style={{ fontFamily: fontDisplay, fontSize: 26, margin: "0 0 8px" }}>{data.name}</h1>

        {data.commentary && (
          <div
            style={{
              background: T.panel,
              borderRadius: 12,
              padding: "14px 18px",
              marginBottom: 14,
              fontSize: 13,
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
              maxWidth: 900,
            }}
          >
            {data.commentary}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <Card label="Covers">
            <span style={{ fontSize: 20, fontFamily: fontDisplay }}>
              {fmtDay(data.fromDate)} — {weeks.length ? fmtDay(isoDate(weeks[weeks.length - 1].end)) : ""}
            </span>
          </Card>
          <Card label="Lowest point">
            <span
              style={{
                fontSize: 26,
                fontFamily: fontDisplay,
                color: lowest && lowest.endBal < 0 ? T.cost : T.ink,
              }}
            >
              {fmtMoney(lowest?.endBal ?? 0)}
            </span>
            <div style={{ fontSize: 11, color: T.muted }}>
              w/e {lowest ? fmtDay(isoDate(lowest.end)) : ""}
            </div>
          </Card>
          <Card label="Closes at">
            <span style={{ fontSize: 26, fontFamily: fontDisplay }}>
              {fmtMoney(weeks.length ? weeks[weeks.length - 1].endBal : 0)}
            </span>
          </Card>
        </div>

        <div
          style={{
            fontSize: 11,
            color: T.muted,
            background: "#F4F1FA",
            border: `1px solid ${T.pipeline}`,
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
            maxWidth: 900,
            lineHeight: 1.6,
          }}
        >
          This is a scenario, not the position. It was copied from the live forecast on{" "}
          {fmtDay(data.createdAt)} and then adjusted — {model.changed} change
          {model.changed === 1 ? "" : "s"} were made to it. Anything invented for the scenario is
          marked <strong style={{ color: T.pipeline }}>speculative</strong>.
        </div>

        <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...nameCell, textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 }}>
                  WEEK
                </th>
                {weeks.map((w, i) => (
                  <th key={i} style={{ ...num, fontFamily: "inherit", fontWeight: 500 }}>
                    {fmtWeek(w.start)}
                    <div style={{ fontSize: 9, color: T.muted }}>W{fyWeek(w.start).week}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Section title="Income" span={weeks.length} />
              {income.map((r) => (
                <Row key={`in-${r.key}`} r={r} weeks={weeks} colour={T.income} nameCell={nameCell} num={num} />
              ))}
              <tr style={{ background: "#EDF3ED", borderTop: `1px solid ${T.border}` }}>
                <td style={{ ...nameCell, fontWeight: 600, color: T.income, background: "#EDF3ED" }}>
                  Total income
                </td>
                {weeks.map((w, i) => (
                  <td key={i} style={{ ...num, fontWeight: 600, color: T.income }}>
                    {colTotal(income, i) ? fmtMoney(colTotal(income, i)) : "–"}
                  </td>
                ))}
              </tr>

              <Section title="Costs" span={weeks.length} />
              {costs.map((r) => (
                <Row key={`out-${r.key}`} r={r} weeks={weeks} colour={T.cost} nameCell={nameCell} num={num} />
              ))}
              <tr style={{ background: "#F7E9E9", borderTop: `1px solid ${T.border}` }}>
                <td style={{ ...nameCell, fontWeight: 600, color: T.cost, background: "#F7E9E9" }}>
                  Total costs
                </td>
                {weeks.map((w, i) => (
                  <td key={i} style={{ ...num, fontWeight: 600, color: T.cost }}>
                    {colTotal(costs, i) ? fmtMoney(Math.abs(colTotal(costs, i))) : "–"}
                  </td>
                ))}
              </tr>

              <tr style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ ...nameCell, fontWeight: 600 }}>Closing balance</td>
                {weeks.map((w, i) => {
                  const past = data.overdraftLimit && w.endBal < -data.overdraftLimit;
                  return (
                    <td
                      key={i}
                      style={{
                        ...num,
                        fontWeight: 700,
                        color: w.endBal < 0 ? T.cost : T.ink,
                        background: past ? "#F0C9C9" : w.endBal < 0 ? "#F7E9E9" : "#EAF0EA",
                      }}
                    >
                      {fmtMoney(w.endBal)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'Manrope', system-ui, sans-serif",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <img src={syrupLogo} alt="Syrup" style={{ height: 42, marginBottom: 20 }} />
        {children}
      </div>
    </div>
  );
}

function Card({ label, children }) {
  return (
    <div style={{ background: T.panel, borderRadius: 12, padding: "14px 18px", flex: "1 1 200px" }}>
      <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Section({ title, span }) {
  return (
    <tr>
      <td style={{ padding: "6px 10px", fontStyle: "italic", fontSize: 11, color: T.muted, background: T.panelSoft }}>
        {title}
      </td>
      <td colSpan={span} style={{ background: T.panelSoft }} />
    </tr>
  );
}

function Row({ r, weeks, colour, nameCell, num }) {
  return (
    <tr style={{ borderTop: `1px solid ${T.borderSoft}` }}>
      <td style={nameCell}>
        {r.key}
        {r.speculative && (
          <span style={{ color: T.pipeline, fontSize: 9, marginLeft: 6, fontWeight: 700 }}>
            SPECULATIVE
          </span>
        )}
      </td>
      {weeks.map((w, i) => (
        <td key={i} style={{ ...num, color: r.cells[i] ? colour : T.border }}>
          {r.cells[i] ? fmtMoney(Math.abs(r.cells[i])) : "–"}
        </td>
      ))}
    </tr>
  );
}
